import api from './api';
import {
  AutopilotSession,
  AutopilotStatus,
  ChallengeProgress,
  KazeFuturesSignal,
  KazeSignalsMeta,
  TopStepAccountStatus,
  TopStepContract,
  TopStepInstrumentRegistryResponse,
  TopStepOrder,
  TopStepPositionsResponse,
  TopStepRiskState,
} from './topstepService';
import { TopStepAccountApi } from '../pages/TopStepAccountPage';

const BASE = '/customer/topstep';

const customerTopstepService: TopStepAccountApi = {
  async getAccounts(): Promise<TopStepAccountStatus[]> {
    const res = await api.get(`${BASE}/accounts/status`);
    const accounts = res.data.data?.accounts;
    return Array.isArray(accounts) ? accounts : [];
  },

  async getAccountRisk(accountId: number): Promise<TopStepRiskState | null> {
    try {
      const res = await api.get(`${BASE}/accounts/${accountId}/risk`);
      return res.data.data || null;
    } catch { return null; }
  },

  async getAccountChallengeProgress(accountId: number): Promise<ChallengeProgress | null> {
    try {
      const res = await api.get(`${BASE}/accounts/${accountId}/risk/progress`);
      return res.data.data || null;
    } catch { return null; }
  },

  async getAccountAutopilotStatus(accountId: number): Promise<AutopilotStatus | null> {
    try {
      const res = await api.get(`${BASE}/accounts/${accountId}/autopilot/status`);
      return res.data.data || null;
    } catch { return null; }
  },

  async getAccountOrders(accountId: number, status?: number): Promise<TopStepOrder[]> {
    try {
      const qs = status !== undefined ? `?status=${status}` : '';
      const res = await api.get(`${BASE}/accounts/${accountId}/orders${qs}`);
      return res.data.data || [];
    } catch { return []; }
  },

  async getAccountPositions(accountId: number): Promise<TopStepPositionsResponse> {
    try {
      const res = await api.get(`${BASE}/accounts/${accountId}/positions`);
      return res.data.data;
    } catch { return { positions: [], totalUnrealized: 0, pnlIsRealtime: true, realtimeCount: 0 }; }
  },

  async getContracts(): Promise<{ contracts: TopStepContract[]; registry: { symbol: string; id: string; name: string; tickSize: number; tickValue: number }[] }> {
    const res = await api.get(`${BASE}/contracts`);
    return res.data.data;
  },

  async getInstruments(): Promise<TopStepInstrumentRegistryResponse> {
    const res = await api.get(`${BASE}/instruments`);
    return {
      defaults: res.data.defaults || [],
      instruments: res.data.instruments || [],
    };
  },

  async getKazeFuturesSignals(): Promise<KazeFuturesSignal[]> {
    const res = await api.get(`${BASE}/kaze/signals`);
    if (res.data?.success === false) throw new Error(res.data.error || 'Kaze signal request failed');
    const signals = res.data.data?.signals;
    return Array.isArray(signals) ? signals : [];
  },

  async getKazeSignalsMeta(): Promise<KazeSignalsMeta | null> {
    const res = await api.get(`${BASE}/kaze/signals`);
    if (res.data?.success === false) throw new Error(res.data.error || 'Kaze signal request failed');
    return res.data.data?.meta || null;
  },

  async startAccountAutopilot(accountId: number, instruments: string[] = []): Promise<AutopilotSession | null> {
    try {
      const res = await api.post(`${BASE}/accounts/${accountId}/start`, {
        instruments,
        confirmLiveTrading: 'I_UNDERSTAND_LIVE_TRADING',
      });
      return res.data.data || null;
    } catch { return null; }
  },

  async stopAccountAutopilot(accountId: number): Promise<AutopilotSession | null> {
    try {
      const res = await api.post(`${BASE}/accounts/${accountId}/stop`);
      return res.data.data || null;
    } catch { return null; }
  },

  async clearAccountKillSwitch(accountId: number): Promise<void> {
    await api.post(`${BASE}/accounts/${accountId}/risk/kill-switch/clear`);
  },

  async placeAccountOrder(accountId: number, params: {
    contractSymbol: string;
    side: 'BUY' | 'SELL';
    contracts: number;
    type?: 'market' | 'limit';
    limitPrice?: number;
  }): Promise<TopStepOrder> {
    const res = await api.post(`${BASE}/accounts/${accountId}/order/place`, params);
    return res.data.data;
  },

  async cancelOrder(_orderId: number): Promise<boolean> {
    return false;
  },

  async cancelAccountOrder(accountId: number, orderId: number): Promise<boolean> {
    const res = await api.post(`${BASE}/accounts/${accountId}/order/cancel`, { orderId });
    return res.data.success;
  },
};

export default customerTopstepService;